import { Link } from "react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, useCan } from "../auth/AuthProvider";
import { GettingStarted } from "../components/GettingStarted";
import { RegionTag, spansRegions } from "../components/RegionTag";
import { Icon } from "../components/icons";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingBlock,
  PageHeader,
  StateBadge,
  buttonStyles,
  cx,
} from "../components/ui";
import { QuotaMeter } from "../components/Quota";
import { describeSource, relativeTime } from "../lib/format";
import { useApps } from "../lib/queries";
import type { AppState } from "../lib/types";

/** How many to fetch at a time, and how many more each "Show more". */
const PAGE = 25;

/*
 * The filter's rungs. `draft` and `stopped` collapse into one because the
 * distinction does not matter to the question being asked here — "what is not
 * up" — and five chips is already more than anyone scans.
 */
type Filter = "all" | "running" | "deploying" | "idle" | "error";

const FILTER_STATES: Record<Exclude<Filter, "all">, AppState[]> = {
  running: ["running"],
  deploying: ["deploying"],
  idle: ["draft", "stopped"],
  error: ["error"],
};

/*
 * The rungs in the order they are shown, which is the only thing about them
 * that is not copy.
 *
 * Two `Record`s of English sentences used to sit here, and they were the
 * filter's order as well as its words — so the order could not be read without
 * reading the copy. The words are in `apps.json` now, keyed by the same rung
 * name the states above use, and this is the list.
 */
const FILTERS: Filter[] = ["all", "running", "deploying", "idle", "error"];

export function AppsPage() {
  const { t } = useTranslation("apps");
  const { currentOrg } = useAuth();
  const canCreate = useCan("developer");
  const [limit, setLimit] = useState(PAGE);
  const [filter, setFilter] = useState<Filter>("all");
  const { data, isPending, isPlaceholderData, error, refetch } = useApps(currentOrg?.id, limit);
  const apps = data?.data ?? [];
  const total = data?.total ?? 0;

  const hasApps = apps.length > 0;
  // A previous empty response cannot prove the inventory is still empty after a
  // failed refresh. Only show that empty state when the current request agrees.
  const loadFailed = Boolean(error && !hasApps);

  const counts = useMemo(() => {
    const tally: Record<Filter, number> = {
      all: apps.length,
      running: 0,
      deploying: 0,
      idle: 0,
      error: 0,
    };
    for (const app of apps) {
      for (const [key, states] of Object.entries(FILTER_STATES)) {
        if (states.includes(app.status.value)) tally[key as Exclude<Filter, "all">] += 1;
      }
    }
    return tally;
  }, [apps]);

  const visible = useMemo(
    () =>
      filter === "all"
        ? apps
        : apps.filter((app) => FILTER_STATES[filter].includes(app.status.value)),
    [apps, filter],
  );

  // The query is paged, so the snapshot is explicit about when it is only a slice.
  const partial = total > apps.length;
  // Region on each row only once there is more than one to tell apart.
  const regioned = spansRegions(apps);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={currentOrg ? t("description", { organization: currentOrg.name }) : undefined}
        action={
          canCreate ? (
            <div className="flex flex-col items-end gap-1.5">
              <Link to="/apps/new" className={buttonStyles("primary")}>
                <Icon name="plus" className="size-3.5" />
                {t("create")}
              </Link>
              <QuotaMeter orgId={currentOrg?.id} resource="apps" />
            </div>
          ) : undefined
        }
      />

      {error && hasApps && (
        <Alert tone="warning">{t("refreshFailed")}</Alert>
      )}

      {/*
        The checklist sits above the list rather than replacing it. It used to be
        the empty state, which meant the guidance disappeared the moment a first
        app existed — the point at which someone has proved they can deploy once
        and still has not connected a git host. It hides itself when every
        required step is done.
      */}
      {!isPending && !loadFailed && (
        <GettingStarted orgId={currentOrg?.id} canCreate={canCreate} hasApps={hasApps} />
      )}

      {/*
         This is an operational snapshot, not dashboard furniture: each cell is
         a filter for the state someone needs to investigate. It replaces five
         nearly identical chips with a readable first scan of the fleet.
      */}
      {hasApps && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-px border border-line bg-line sm:grid-cols-5">
            {FILTERS.map((key) => {
              const count = counts[key];
              const isActive = filter === key;
              const isBad = key === "error" && count > 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  aria-pressed={isActive}
                  className={cx(
                    "bg-canvas px-4 py-3.5 text-left transition-colors duration-200 ease-smooth hover:bg-raised",
                    key === "all" && "col-span-2 sm:col-span-1",
                    isActive ? "bg-raised" : "text-fg-muted",
                    /* A failing app is the one count that should be findable
                        without reading the label beside it. */
                    isBad && "text-danger",
                  )}
                >
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="text-[13px] font-medium">{t(`filters.${key}.label`)}</span>
                    <span
                      className={cx(
                        "text-lg font-medium tracking-tight tabular-nums",
                        isBad ? "text-danger" : "text-fg",
                      )}
                    >
                      {count}
                    </span>
                  </span>
                  <span className="mt-1 block text-[11px] text-fg-subtle">
                    {t(`filters.${key}.summary`)}
                  </span>
                </button>
              );
            })}
          </div>

          {partial && (
            /* Said out loud, because a filtered list that silently covers only
                the first 25 of 60 apps is a wrong answer presented as a right
                one. */
            <p className="text-right text-[11px] text-fg-subtle">
              {t("partial", { shown: apps.length, total })}
            </p>
          )}
        </div>
      )}

      <Card>
        {isPending ? (
          <LoadingBlock />
        ) : loadFailed ? (
          <EmptyState
            icon="apps"
            title={t("loadFailed.title")}
            description={t("loadFailed.description")}
            action={
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                {t("loadFailed.retry")}
              </Button>
            }
          />
        ) : !hasApps ? (
          /*
            "No apps yet" on its own is true and useless. This says what an app
            is, because a first-time customer does not necessarily know that a
            frontend and a backend are two of them.
          */
          <EmptyState
            icon="apps"
            title={t("empty.title")}
            description={t("empty.description")}
            action={
              canCreate ? (
                <Link to="/apps/new" className={buttonStyles("primary")}>
                  <Icon name="plus" className="size-3.5" />
                  {t("empty.create")}
                </Link>
              ) : (
                <p className="text-[13px] text-fg-subtle">{t("empty.noAccess")}</p>
              )
            }
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="apps"
            /*
             * The rung's own name, not a lowercased one.
             *
             * This built the sentence by lowercasing the filter's label, which
             * reads as English grammar applied to a noun — and in a language
             * that capitalises differently, or inflects the word inside a
             * sentence, produces something between wrong and meaningless. The
             * whole heading is one key per rung instead.
             */
            title={t(`noMatch.title`, { filter: t(`filters.${filter}.label`).toLocaleLowerCase() })}
            description={partial ? t("noMatch.partial", { count: apps.length }) : undefined}
            action={
              <Button variant="secondary" size="sm" onClick={() => setFilter("all")}>
                {t("noMatch.showAll", { count: apps.length })}
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {visible.map((app) => (
              <li key={app.id}>
                <Link
                  to={`/apps/${app.id}`}
                  className="group flex flex-wrap items-center gap-3 px-5 py-3.5 transition-colors duration-200 ease-smooth hover:bg-raised sm:flex-nowrap sm:gap-4"
                >
                  {/* Hover used to fill this tile with solid accent, which made
                      blue mean "hovered" on the one page where it otherwise
                      means "a link". A hairline lifting is enough. */}
                  <span className="grid size-8 shrink-0 place-items-center rounded-md border border-line bg-raised text-fg-subtle transition-colors duration-200 ease-smooth group-hover:border-line-strong group-hover:text-fg">
                    <Icon name="apps" className="size-3.5" />
                  </span>

                  <div className="min-w-0 flex-1 max-sm:basis-[calc(100%-2.75rem)]">
                    <p className="truncate text-[13px] text-fg">{app.name}</p>
                    <p className="ident truncate">{describeSource(app.source)}</p>
                  </div>

                  {regioned && <RegionTag region={app.region} />}

                  {/* Dropped first when the row runs out of room: the state
                      badge and the app's name matter more than the timestamp. */}
                  <span className="hidden text-[13px] text-fg-subtle sm:inline">
                    {relativeTime(app.updatedAt)}
                  </span>

                  <span className="max-sm:ml-auto">
                    <StateBadge state={app.status.value} stale={app.status.stale} />
                  </span>

                  <Icon
                    name="chevron"
                    className="size-3.5 shrink-0 text-fg-subtle transition-transform duration-200 ease-smooth group-hover:translate-x-0.5"
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}

        {partial && (
          <div className="border-t border-line p-2.5">
            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              loading={isPlaceholderData}
              onClick={() => setLimit((current) => current + PAGE)}
            >
              {t("more", { count: total - apps.length })}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
